// Project 8: 발행 진행 상황 모달
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { publishService } from '@/services/PublishService';
import type { PublishPlatform } from '@/types/publish';

interface PublishStatusModalProps {
  jobIds: Record<PublishPlatform, string>;
  isOpen: boolean;
  onClose: () => void;
}

export function PublishStatusModal({ jobIds, isOpen, onClose }: PublishStatusModalProps) {
  const [statuses, setStatuses] = useState<Record<string, any>>({});

  // 각 작업의 상태를 폴링
  const platformKeys = Object.keys(jobIds) as PublishPlatform[];

  useEffect(() => {
    if (!isOpen || platformKeys.length === 0) return;

    const fetchStatuses = async () => {
      const results: Record<string, any> = {};
      for (const platform of platformKeys) {
        const jobId = jobIds[platform];
        if (jobId) {
          try {
            const response = await publishService.getStatus(jobId);
            if (response.success && response.data) {
              results[platform] = response.data;
            }
          } catch (error) {
            console.error(`Status fetch error for ${platform}:`, error);
          }
        }
      }
      setStatuses(results);
    };

    fetchStatuses();
    const interval = setInterval(fetchStatuses, 2000); // 2초마다 폴링

    return () => clearInterval(interval);
  }, [isOpen, JSON.stringify(jobIds)]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return { color: '#00b894', bg: '#d4edda' };
      case 'failed':
        return { color: '#d63031', bg: '#fee' };
      case 'processing':
        return { color: '#fdcb6e', bg: '#fff3cd' };
      case 'retrying':
        return { color: '#e17055', bg: '#ffe5e5' };
      default:
        return { color: '#999', bg: '#f0f0f0' };
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'success':
        return '성공 ✓';
      case 'failed':
        return '실패 ✗';
      case 'processing':
        return '처리 중...';
      case 'retrying':
        return '재시도 중...';
      case 'pending':
        return '대기 중';
      default:
        return status;
    }
  };

  const allCompleted = platformKeys.every(
    (platform) => statuses[platform]?.status === 'success' || statuses[platform]?.status === 'failed'
  );

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 8,
        padding: '2rem',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0 }}>발행 진행 상황</h2>
          {allCompleted && (
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem',
                background: '#2196f3',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              닫기
            </button>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {platformKeys.map((platform) => {
            const status = statuses[platform];
            const statusColor = status ? getStatusColor(status.status) : { color: '#999', bg: '#f0f0f0' };
            const statusLabel = status ? getStatusLabel(status.status) : '조회 중...';

            return (
              <div
                key={platform}
                style={{
                  padding: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  background: statusColor.bg
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 600, fontSize: '1rem' }}>
                    {platform === 'wordpress' ? '📝 WordPress' :
                     platform === 'tistory' ? '📔 티스토리' :
                     '📘 네이버 블로그'}
                  </span>
                  <span style={{
                    color: statusColor.color,
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    {statusLabel}
                  </span>
                </div>

                {status?.remoteUrl && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <a
                      href={status.remoteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#2196f3',
                        textDecoration: 'none',
                        fontSize: '0.875rem'
                      }}
                    >
                      발행된 포스트 보기 →
                    </a>
                  </div>
                )}

                {status?.errorMessage && (
                  <div style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem',
                    background: '#fee',
                    borderRadius: 4,
                    fontSize: '0.875rem',
                    color: '#c00'
                  }}>
                    오류: {status.errorMessage}
                  </div>
                )}

                {status?.retryCount > 0 && (
                  <div style={{
                    marginTop: '0.5rem',
                    fontSize: '0.75rem',
                    color: '#666'
                  }}>
                    재시도 횟수: {status.retryCount}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

